import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import favoritesService from "../services/favoritesService";
import logger from "../utils/logger";
import { toast } from "sonner";

/**
 * Custom hook for managing user favorites
 * Efficiently loads user's favorites once and provides local lookup
 */
export const useFavorites = () => {
  const { isLoggedIn } = useAuth();
  const [favoriteGames, setFavoriteGames] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user's favorite games once when they log in
  const loadUserFavorites = useCallback(async () => {
    if (!isLoggedIn) {
      setFavoriteGames(new Set());
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get user's favorite games (limited to 20 max)
      const favoritesResponse = await favoritesService.getFavoriteGames({
        limit: 20,
      });
      const favoritedCodes =
        favoritesResponse.data?.games?.map((game) => game.game_code) || [];

      setFavoriteGames(new Set(favoritedCodes));

      logger.info("User favorites loaded", {
        favoritedGames: favoritedCodes.length,
      });
    } catch (err) {
      logger.error("Failed to load user favorites", err);
      setError("Failed to load favorites");
      setFavoriteGames(new Set());
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // Load favorites when user logs in/out
  useEffect(() => {
    loadUserFavorites();
  }, [isLoggedIn, loadUserFavorites]);

  // Toggle favorite status for a game
  const toggleFavorite = useCallback(
    async (gameCode) => {
      if (!isLoggedIn) {
        toast.error("Please log in to add favorites");
        return false;
      }

      const currentStatus = favoriteGames.has(gameCode);

      // Check if user is trying to add a favorite when they already have 20
      if (!currentStatus && favoriteGames.size >= 20) {
        toast.error(
          "You can only have up to 20 favorite games. Remove some favorites first.",
        );
        return false;
      }

      try {
        // Optimistically update UI
        setFavoriteGames((prev) => {
          const newSet = new Set(prev);
          if (currentStatus) {
            newSet.delete(gameCode);
          } else {
            newSet.add(gameCode);
          }
          return newSet;
        });

        // Make API call
        const result = await favoritesService.toggleFavorite(
          gameCode,
          currentStatus,
        );

        // Show success message
        if (result.data?.favorited) {
          toast.success("Added to favorites!");
        } else {
          toast.success("Removed from favorites");
        }

        logger.info("Favorite toggled successfully", {
          gameCode,
          newStatus: result.data?.favorited,
        });

        return result.data?.favorited;
      } catch (err) {
        // Revert optimistic update on error
        setFavoriteGames((prev) => {
          const newSet = new Set(prev);
          if (currentStatus) {
            newSet.add(gameCode); // Revert removal
          } else {
            newSet.delete(gameCode); // Revert addition
          }
          return newSet;
        });

        logger.error("Failed to toggle favorite", err, { gameCode });
        toast.error("Failed to update favorites");
        return currentStatus;
      }
    },
    [isLoggedIn, favoriteGames],
  );

  // Add a game to favorites
  const addToFavorites = useCallback(
    async (gameCode) => {
      if (!isLoggedIn) {
        toast.error("Please log in to add favorites");
        return false;
      }

      if (favoriteGames.has(gameCode)) {
        return true; // Already favorited
      }

      return await toggleFavorite(gameCode);
    },
    [isLoggedIn, favoriteGames, toggleFavorite],
  );

  // Remove a game from favorites
  const removeFromFavorites = useCallback(
    async (gameCode) => {
      if (!isLoggedIn) {
        return false;
      }

      if (!favoriteGames.has(gameCode)) {
        return true; // Already not favorited
      }

      return !(await toggleFavorite(gameCode));
    },
    [isLoggedIn, favoriteGames, toggleFavorite],
  );

  // Check if a game is favorited
  const isFavorited = useCallback(
    (gameCode) => {
      return favoriteGames.has(gameCode);
    },
    [favoriteGames],
  );

  // Refresh favorites from server
  const refreshFavorites = useCallback(() => {
    loadUserFavorites();
  }, [loadUserFavorites]);

  return {
    favoriteGames,
    loading,
    error,
    toggleFavorite,
    addToFavorites,
    removeFromFavorites,
    isFavorited,
    refreshFavorites,
    isLoggedIn,
  };
};
